import { css } from 'aphrodite';
import * as React from 'react';
import { ILeadViewFilter } from '../../../entities/LeadView/useLeadView';
import { useManagerPermissions } from '../../../hooks/useManagerPermissions';
import { ILeadServedOutcomeView } from '../../../viewModels/leads/interfaces';
import { useLeadServedFilterInfiniteQuery } from '../../../../queries/LeadServed/useLeadServedFilterInfiniteQuery';
import { CALL_LOG_COLUMN_LABELS, CallLogRow } from './CallLogRow';
import { InfiniteListStates } from './InfiniteListStates';
import { styleSheet } from './styles';

// A lead is served repeatedly and a call isn't guaranteed one row, so only a full match is a repeated row.
const callKey = (call: ILeadServedOutcomeView, index: number) => {
	const parts = [call.id, call.phoneCall?.id, call.interaction?.date].filter(Boolean);
	return parts.length ? parts.join('|') : `call-${index}`;
};

interface IProps {
	filter: ILeadViewFilter;
	selectedCallId?: string;
	onOpenCall: (callId: string, options?: { autoplay?: boolean }) => void;
	onOpenSdrGoals: (userId: string, userName: string) => void;
}

export const CallLogList: React.FC<IProps> = ({ filter, selectedCallId, onOpenCall, onOpenSdrGoals }) => {
	const { hasManagerPermissions } = useManagerPermissions();
	const query = useLeadServedFilterInfiniteQuery({ filter });
	const calls = React.useMemo(() => {
		const seen = new Set<string>();
		return (query.data?.pages.flatMap(page => page.values ?? []) ?? []).filter((call, index) => {
			const key = callKey(call, index);
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	}, [query.data]);

	return (
		<InfiniteListStates
			isEmpty={!calls.length}
			error={query.error}
			isLoading={query.isLoading}
			isFetching={query.isFetching}
			isRefetchError={query.isRefetchError}
			isFetchingNextPage={query.isFetchingNextPage}
			isFetchNextPageError={query.isFetchNextPageError}
			hasNextPage={query.hasNextPage}
			refetch={query.refetch}
			fetchNextPage={query.fetchNextPage}
			loadingLabel='Loading calls'
			emptyText='No calls match these filters'
			header={
				<div className={css(styleSheet.columns, styleSheet.callLogGrid)} aria-hidden='true'>
					{CALL_LOG_COLUMN_LABELS.map((label, index) => (
						<span
							key={label}
							className={index === CALL_LOG_COLUMN_LABELS.length - 1 ? css(styleSheet.hideBelowFullRow) : undefined}
						>
							{label}
						</span>
					))}
				</div>
			}
			errorText="We couldn't load your calls."
		>
			<div className={css(styleSheet.list)}>
				{calls.map((call, index) => (
					<CallLogRow
						key={callKey(call, index)}
						call={call}
						canOpenSdrGoals={hasManagerPermissions}
						isSelected={!!selectedCallId && call.phoneCall?.id === selectedCallId}
						onOpenCall={onOpenCall}
						onOpenSdrGoals={onOpenSdrGoals}
					/>
				))}
			</div>
		</InfiniteListStates>
	);
};
